import React from 'react';
import {
  FileText,
  Sparkles,
  Copy,
  Building2,
  ChevronRight,
  FileUp,
  Link2,
  Plus,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const CreateWorkflowMenu = ({
  onClose,
  onSelect,
  onImportFromFile,
  onImportFromUrl,
}) => {
  const options = [
    {
      id: 'scratch',
      icon: Plus,
      label: 'Start from Scratch',
      description: 'Build a custom workflow from the ground up',
      color: 'teal',
    },
    {
      id: 'ai',
      icon: Sparkles,
      label: 'Build with AI',
      description: 'Describe your workflow and let AI create it',
      color: 'purple',
      badge: 'Beta',
    },
    {
      id: 'template',
      icon: FileText,
      label: 'Select from Template',
      description: 'Start with a pre-built workflow template',
      color: 'blue',
    },
    {
      id: 'duplicate',
      icon: Copy,
      label: 'Duplicate Existing Workflow',
      description: 'Copy and modify an existing workflow',
      color: 'amber',
    },
    {
      id: 'agency-wide',
      icon: Building2,
      label: 'Agency-Wide Workflow',
      description: 'Create a workflow that applies to all clients',
      color: 'emerald',
    },
  ];

  const colorClasses = {
    teal: 'bg-teal-100 text-teal-600 group-hover:bg-teal-200',
    purple: 'bg-purple-100 text-purple-600 group-hover:bg-purple-200',
    blue: 'bg-blue-100 text-blue-600 group-hover:bg-blue-200',
    amber: 'bg-amber-100 text-amber-600 group-hover:bg-amber-200',
    emerald: 'bg-emerald-100 text-emerald-600 group-hover:bg-emerald-200',
  };

  const handleImportFile = () => {
    onClose?.();
    if (typeof onImportFromFile === 'function') {
      onImportFromFile();
      return;
    }
    onSelect?.('import-file');
  };

  const handleImportUrl = () => {
    onClose?.();
    if (typeof onImportFromUrl === 'function') {
      onImportFromUrl();
      return;
    }
    onSelect?.('import-url');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />

      {/* Menu */}
      <div
        className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden"
        data-testid="create-workflow-menu"
      >
        <div className="p-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Create Workflow</h3>
          <p className="text-xs text-slate-500 mt-0.5">Choose how to start your workflow</p>
        </div>

        <div className="p-2">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => onSelect(option.id)}
              className="group w-full flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
              data-testid={`create-option-${option.id}`}
            >
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                colorClasses[option.color]
              )}>
                <option.icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{option.label}</span>
                  {option.badge && (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-600 rounded">
                      {option.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{option.description}</p>
              </div>
              <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-400 mt-2.5 flex-shrink-0" />
            </button>
          ))}

          <div className="my-1 border-t border-slate-100" data-testid="create-menu-import-separator" />

          <button
            type="button"
            onClick={handleImportFile}
            className="group w-full flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
            data-testid="create-option-import-file"
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600 group-hover:bg-slate-200">
              <FileUp size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-medium text-slate-900">Import from file</span>
              <p className="text-xs text-slate-500 mt-0.5">
                Upload a FundElite portable workflow JSON
              </p>
            </div>
            <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-400 mt-2.5 flex-shrink-0" />
          </button>

          <button
            type="button"
            onClick={handleImportUrl}
            className="group w-full flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
            data-testid="create-option-import-url"
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600 group-hover:bg-slate-200">
              <Link2 size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-medium text-slate-900">Import from URL</span>
              <p className="text-xs text-slate-500 mt-0.5">
                Fetch a public HTTPS workflow package
              </p>
            </div>
            <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-400 mt-2.5 flex-shrink-0" />
          </button>
        </div>
      </div>
    </>
  );
};

export default CreateWorkflowMenu;
